import React from 'react';
import { AlertCircle, ArrowRight, BookOpenText, Ear, Sparkles, Waves } from 'lucide-react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';

const STATUS_BADGE_CLASSES = {
  urgent: 'border-red-200 bg-red-50 text-red-700 dark:border-red-500/25 dark:bg-red-500/10 dark:text-red-300',
  attention: 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/25 dark:bg-amber-500/10 dark:text-amber-300',
  next: 'border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-500/25 dark:bg-sky-500/10 dark:text-sky-300',
  steady: 'border-slate-200 bg-slate-100 text-slate-700 dark:border-slate-400/20 dark:bg-slate-400/10 dark:text-slate-300',
};

const METRIC_CARD_CLASSES = {
  urgent: 'border-red-200 bg-red-50/90 dark:border-red-500/20 dark:bg-red-500/5',
  attention: 'border-amber-200 bg-amber-50/90 dark:border-amber-500/20 dark:bg-amber-500/5',
  next: 'border-sky-200 bg-sky-50/90 dark:border-sky-500/20 dark:bg-sky-500/5',
  steady: 'border-slate-200 bg-white/90 dark:border-white/10 dark:bg-white/[0.03]',
};

function StageBadge({ tone, children }) {
  return (
    <Badge className={STATUS_BADGE_CLASSES[tone] || STATUS_BADGE_CLASSES.steady}>
      {children}
    </Badge>
  );
}

function MetricCard({ metric }) {
  return (
    <Card className={`rounded-[1.2rem] border p-4 ${METRIC_CARD_CLASSES[metric.tone] || METRIC_CARD_CLASSES.steady}`}>
      <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500 dark:text-gray-500">
        {metric.label}
      </p>
      <p className="mt-3 text-3xl font-semibold text-slate-900 dark:text-white">
        {metric.value}
      </p>
      <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-gray-300">
        {metric.detail}
      </p>
    </Card>
  );
}

function UrgentItemCard({ item }) {
  return (
    <div className="rounded-[1.15rem] border border-amber-200 bg-white/90 p-4 shadow-[0_20px_45px_-38px_rgba(180,83,9,0.45)] dark:border-amber-500/15 dark:bg-black/10">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-base font-semibold text-slate-900 dark:text-white">
            {item.title}
          </p>
          <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-gray-500">
            {item.detail}
          </p>
        </div>
        <StageBadge tone={item.tone}>
          {item.actionLabel}
        </StageBadge>
      </div>
      <p className="mt-4 text-sm leading-6 text-slate-700 dark:text-gray-300">
        “{item.excerpt}”
      </p>
      <p className="mt-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-gray-500">
        {item.location}
      </p>
    </div>
  );
}

function PatternCard({ pattern }) {
  return (
    <div className="rounded-[1.15rem] border border-sky-200 bg-white/90 p-4 shadow-[0_20px_45px_-38px_rgba(14,116,144,0.35)] dark:border-sky-500/15 dark:bg-black/10">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-base font-semibold text-slate-900 dark:text-white">
            {pattern.label}
          </p>
          <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-gray-300">
            {pattern.description}
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <StageBadge tone={pattern.tone}>
            {pattern.count} in view
          </StageBadge>
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-gray-500">
            {pattern.route}
          </p>
        </div>
      </div>

      {pattern.sampleExcerpts?.length ? (
        <div className="mt-4 space-y-2">
          {pattern.sampleExcerpts.map((excerpt, index) => (
            <div key={`${pattern.id}-${index}`} className="rounded-xl border border-slate-200 bg-slate-50/85 px-3 py-2.5 text-sm leading-6 text-slate-700 dark:border-white/10 dark:bg-white/[0.04] dark:text-gray-300">
              “{excerpt}”
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function ActionCard({ action }) {
  return (
    <div className={`rounded-[1.15rem] border p-4 ${METRIC_CARD_CLASSES[action.tone] || METRIC_CARD_CLASSES.steady}`}>
      <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500 dark:text-gray-500">
        {action.eyebrow}
      </p>
      <h4 className="mt-3 text-base font-semibold text-slate-900 dark:text-white">
        {action.title}
      </h4>
      <p className="mt-2 text-sm leading-6 text-slate-700 dark:text-gray-300">
        {action.detail}
      </p>
      <p className="mt-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-gray-500">
        {action.location}
      </p>
    </div>
  );
}

export default function ListenLearnRoom({ context }) {
  return (
    <div className="grid gap-4">
      <Card className="border-slate-200 bg-gradient-to-br from-white via-[#fbfcfd] to-[#f4f7f8] p-5 dark:border-white/10 dark:bg-gradient-to-br dark:from-[#20232a] dark:via-[#171b20] dark:to-[#11161a]">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
            <div className="max-w-3xl">
              <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-sky-700 dark:text-sky-300">
                Feedback & Insight operator room
              </p>
              <h3 className="mt-3 text-2xl font-semibold leading-8 text-slate-900 dark:text-white">
                {context.startTitle}
              </h3>
              <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-gray-300">
                {context.startDetail}
              </p>
              {context.analysisSummary ? (
                <div className="mt-4 rounded-[1rem] border border-sky-200 bg-sky-50/85 px-4 py-3 dark:border-sky-500/15 dark:bg-sky-500/8">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-sky-700 dark:text-sky-300">
                    Analysis summary
                  </p>
                  <p className="mt-2 text-sm leading-6 text-slate-700 dark:text-gray-300">
                    {context.analysisSummary}
                  </p>
                </div>
              ) : null}
            </div>
            <StageBadge tone={context.statusTone}>
              {context.statusLabel}
            </StageBadge>
          </div>

          <div className="grid gap-2 md:grid-cols-3">
            {(context.signals || []).map((signal, index) => (
              <div key={`${signal.text}-${index}`} className="rounded-2xl border border-slate-200 bg-white/80 px-3 py-3 dark:border-white/10 dark:bg-white/[0.04]">
                <p className="text-sm leading-6 text-slate-700 dark:text-gray-300">
                  {signal.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </Card>

      <div className="grid gap-3 md:grid-cols-3">
        {(context.metrics || []).map((metric, index) => (
          <MetricCard key={`${metric.label}-${index}`} metric={metric} />
        ))}
      </div>

      <div className="grid gap-4 xl:grid-cols-[1.05fr_0.95fr]">
        <Card className="border-amber-200 bg-gradient-to-br from-[#fff8ed] via-[#fffbf4] to-[#f7efe3] p-5 dark:border-amber-500/15 dark:bg-gradient-to-br dark:from-[#241d14] dark:via-[#1c1712] dark:to-[#141312]">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.26em] text-amber-700 dark:text-amber-300">
                Catch pain early
              </p>
              <h4 className="mt-3 text-xl font-semibold text-slate-900 dark:text-white">
                Surface what may need human attention first
              </h4>
              <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-gray-300">
                These are the harder signals: discouraged language, confusion, burden, or feedback tied to at-risk dads.
              </p>
            </div>
            <div className="rounded-full bg-white/75 p-3 text-amber-700 shadow-sm dark:bg-black/20 dark:text-amber-300">
              <AlertCircle className="h-5 w-5" />
            </div>
          </div>

          <div className="mt-5 space-y-3">
            {context.urgentItems?.length ? (
              context.urgentItems.map((item) => (
                <UrgentItemCard key={item.id} item={item} />
              ))
            ) : (
              <div className="rounded-[1.15rem] border border-dashed border-amber-200 bg-white/70 px-4 py-8 text-center dark:border-amber-500/15 dark:bg-black/10">
                <p className="text-base font-semibold text-slate-900 dark:text-white">
                  No hard signals are surfacing right now
                </p>
                <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-gray-300">
                  Feedback is quiet enough that you can step back and look for the next real pattern instead of reacting too early.
                </p>
              </div>
            )}
          </div>
        </Card>

        <Card className="border-sky-200 bg-gradient-to-br from-[#eef8fb] via-[#f6fbfd] to-[#edf3f8] p-5 dark:border-sky-500/15 dark:bg-gradient-to-br dark:from-[#151d21] dark:via-[#11181d] dark:to-[#0f1418]">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.26em] text-sky-700 dark:text-sky-300">
                Patterns in view
              </p>
              <h4 className="mt-3 text-xl font-semibold text-slate-900 dark:text-white">
                Step back and see what is repeating
              </h4>
              <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-gray-300">
                These cards group repeated signals so the room can distinguish isolated pain from a broader pattern worth acting on.
              </p>
            </div>
            <div className="rounded-full bg-white/75 p-3 text-sky-700 shadow-sm dark:bg-black/20 dark:text-sky-300">
              <Waves className="h-5 w-5" />
            </div>
          </div>

          <div className="mt-5 space-y-3">
            {context.patterns?.length ? (
              context.patterns.map((pattern) => (
                <PatternCard key={pattern.id} pattern={pattern} />
              ))
            ) : (
              <div className="rounded-[1.15rem] border border-dashed border-sky-200 bg-white/70 px-4 py-8 text-center dark:border-sky-500/15 dark:bg-black/10">
                <p className="text-base font-semibold text-slate-900 dark:text-white">
                  No repeated pattern is strong enough yet
                </p>
                <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-gray-300">
                  Keep listening. The next theme should emerge from repeated voices, not from one isolated reaction.
                </p>
              </div>
            )}
          </div>
        </Card>
      </div>

      <Card className="border-emerald-200 bg-gradient-to-br from-[#eff9f3] via-[#f8fcfa] to-[#edf8f4] p-5 dark:border-emerald-500/15 dark:bg-gradient-to-br dark:from-[#162019] dark:via-[#121814] dark:to-[#101512]">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="max-w-3xl">
            <p className="text-[11px] font-semibold uppercase tracking-[0.26em] text-emerald-700 dark:text-emerald-300">
              Suggested actions
            </p>
            <h4 className="mt-3 text-xl font-semibold text-slate-900 dark:text-white">
              Suggested next moves, not automatic decisions
            </h4>
            <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-gray-300">
              This queue is advisory in v1. It should help you decide what deserves review, follow-up, adjustment, routing, or simple watchfulness.
            </p>
          </div>
          <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300">
            <Sparkles className="h-5 w-5" />
            <BookOpenText className="h-5 w-5" />
            <Ear className="h-5 w-5" />
          </div>
        </div>

        <div className="mt-5 grid gap-3 xl:grid-cols-3">
          {(context.suggestedActions || []).map((action, index) => (
            <ActionCard key={`${action.title}-${index}`} action={action} />
          ))}
        </div>

        {(context.primaryAction?.label || context.secondaryAction?.label) ? (
          <div className="mt-5 flex flex-wrap items-center gap-2">
            <p className="mr-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500 dark:text-gray-500">
              Suggested flow
            </p>
            {context.primaryAction?.label ? (
              <div className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-white/80 px-3 py-1.5 text-sm text-sky-900 dark:border-sky-500/20 dark:bg-black/20 dark:text-sky-300">
                <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-sky-700 dark:text-sky-300">
                  Start
                </span>
                <span className="font-medium">{context.primaryAction.label}</span>
              </div>
            ) : null}
            {context.secondaryAction?.label ? (
              <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/80 px-3 py-1.5 text-sm text-slate-700 dark:border-white/10 dark:bg-black/20 dark:text-gray-300">
                <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-gray-500">
                  Then
                </span>
                <span className="font-medium">{context.secondaryAction.label}</span>
              </div>
            ) : null}
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-white/70 px-3 py-1.5 text-sm text-emerald-800 dark:border-emerald-500/20 dark:bg-black/20 dark:text-emerald-300">
              <ArrowRight className="h-4 w-4" />
              <span>Investigate below when you need the full detail</span>
            </div>
          </div>
        ) : null}
      </Card>
    </div>
  );
}
